import type { FormValues } from '../models/form';
import { toEnglishNumbers } from './numbers';

interface Token {
  kind: 'number' | 'operator' | 'left' | 'right' | 'reference' | 'comma' | 'identifier';
  value: string;
}

function tokenize(expression: string): Token[] {
  const source = toEnglishNumbers(expression ?? '');
  const tokens: Token[] = [];
  let index = 0;
  while (index < source.length) {
    const current = source[index]!;
    if (/\s/.test(current)) { index += 1; continue; }
    if (current === '[') {
      const end = source.indexOf(']', index + 1);
      if (end < 0) throw new Error('براکت مرجع فیلد بسته نشده است.');
      const reference = source.slice(index + 1, end).trim();
      if (!reference || !/^[A-Za-z0-9_.-]+$/.test(reference)) throw new Error('مرجع فیلد در فرمول معتبر نیست.');
      tokens.push({ kind: 'reference', value: reference });
      index = end + 1;
      continue;
    }
    if (/[0-9.]/.test(current)) {
      let end = index + 1;
      while (end < source.length && /[0-9.]/.test(source[end]!)) end += 1;
      const number = source.slice(index, end);
      if (!/^\d+(?:\.\d+)?$/.test(number) && !/^\.\d+$/.test(number)) throw new Error('عدد نامعتبر در فرمول.');
      tokens.push({ kind: 'number', value: number });
      index = end;
      continue;
    }
    if (/[A-Za-z]/.test(current)) {
      let end = index + 1;
      while (end < source.length && /[A-Za-z]/.test(source[end]!)) end += 1;
      tokens.push({ kind: 'identifier', value: source.slice(index, end).toUpperCase() });
      index = end;
      continue;
    }
    if ('+-*/%'.includes(current)) { tokens.push({ kind: 'operator', value: current }); index += 1; continue; }
    if (current === '(') { tokens.push({ kind: 'left', value: current }); index += 1; continue; }
    if (current === ')') { tokens.push({ kind: 'right', value: current }); index += 1; continue; }
    if (current === ',') { tokens.push({ kind: 'comma', value: current }); index += 1; continue; }
    throw new Error(`کاراکتر «${current}» در فرمول مجاز نیست.`);
  }
  return tokens;
}

function scalarNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'boolean') return value ? 1 : 0;
  const normalized = toEnglishNumbers(String(value ?? '')).replace(/[,٬\s]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function resolveReference(reference: string, values: FormValues, currentTab: string): number {
  const separator = reference.indexOf('.');
  const tab = separator > 0 ? reference.slice(0, separator) : currentTab;
  const field = separator > 0 ? reference.slice(separator + 1) : reference;
  const value = values[tab]?.[field];
  if (Array.isArray(value)) {
    return value.reduce((sum, item) => sum + scalarNumber(item), 0);
  }
  return scalarNumber(value);
}

class Parser {
  private position = 0;
  constructor(private readonly tokens: Token[], private readonly values: FormValues, private readonly currentTab: string) {}

  parse(): number {
    if (!this.tokens.length) return 0;
    const result = this.expression();
    if (this.position !== this.tokens.length) throw new Error('ادامه فرمول قابل پردازش نیست.');
    return Number.isFinite(result) ? result : 0;
  }

  private peek(): Token | undefined { return this.tokens[this.position]; }
  private take(): Token { const token = this.tokens[this.position]; if (!token) throw new Error('پایان غیرمنتظره فرمول.'); this.position += 1; return token; }

  private expression(): number {
    let value = this.term();
    while (this.peek()?.kind === 'operator' && ['+', '-'].includes(this.peek()!.value)) {
      const operator = this.take().value;
      const right = this.term();
      value = operator === '+' ? value + right : value - right;
    }
    return value;
  }

  private term(): number {
    let value = this.unary();
    while (this.peek()?.kind === 'operator' && ['*', '/', '%'].includes(this.peek()!.value)) {
      const operator = this.take().value;
      const right = this.unary();
      if ((operator === '/' || operator === '%') && right === 0) throw new Error('تقسیم بر صفر در فرمول مجاز نیست.');
      if (operator === '*') value *= right;
      else if (operator === '/') value /= right;
      else value %= right;
    }
    return value;
  }

  private unary(): number {
    const next = this.peek();
    if (next?.kind === 'operator' && (next.value === '+' || next.value === '-')) {
      this.take();
      const value = this.unary();
      return next.value === '-' ? -value : value;
    }
    return this.primary();
  }

  private primary(): number {
    const token = this.take();
    if (token.kind === 'number') return Number(token.value);
    if (token.kind === 'reference') return resolveReference(token.value, this.values, this.currentTab);
    if (token.kind === 'left') {
      const value = this.expression();
      if (this.take().kind !== 'right') throw new Error('پرانتز فرمول بسته نشده است.');
      return value;
    }
    if (token.kind === 'identifier') return this.functionCall(token.value);
    throw new Error('بخش نامعتبر در فرمول.');
  }

  private functionCall(name: string): number {
    if (this.take().kind !== 'left') throw new Error(`تابع ${name} باید با پرانتز نوشته شود.`);
    const args: number[] = [];
    if (this.peek()?.kind !== 'right') {
      args.push(this.expression());
      while (this.peek()?.kind === 'comma') { this.take(); args.push(this.expression()); }
    }
    if (this.take().kind !== 'right') throw new Error(`پرانتز تابع ${name} بسته نشده است.`);
    if (name === 'ABS' && args.length === 1) return Math.abs(args[0]!);
    if (name === 'ROUND' && (args.length === 1 || args.length === 2)) {
      const digits = Math.max(0, Math.min(8, Math.trunc(args[1] ?? 0)));
      const factor = 10 ** digits;
      return Math.round(args[0]! * factor) / factor;
    }
    if (name === 'MIN' && args.length) return Math.min(...args);
    if (name === 'MAX' && args.length) return Math.max(...args);
    if (name === 'SUM') return args.reduce((sum, value) => sum + value, 0);
    throw new Error(`تابع ${name} یا تعداد پارامترهای آن پشتیبانی نمی‌شود.`);
  }
}

export function evaluateCalculation(expression: string | undefined, values: FormValues, currentTab: string): number {
  if (!expression?.trim()) return 0;
  return new Parser(tokenize(expression), values, currentTab).parse();
}

export function formatCalculation(value: number, format: 'plain' | 'number' | 'currency' = 'number', decimals = 0, currencyLabel = 'ریال'): string {
  const safeDecimals = Math.max(0, Math.min(8, Math.trunc(decimals)));
  if (format === 'plain') return value.toFixed(safeDecimals);
  const text = value.toLocaleString('fa-IR', { minimumFractionDigits: safeDecimals, maximumFractionDigits: safeDecimals });
  return format === 'currency' ? `${text} ${currencyLabel || 'ریال'}` : text;
}
